import { ApplicationRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MasterContactAddress } from '../../master-contact/models/MasterContactAddress';
import { concat, of, debounceTime, distinctUntilChanged, tap, switchMap, catchError, Observable, Subject } from 'rxjs';
import { MasterContactService } from '../../master-contact/master-contact.service';
import { AsyncPipe, CommonModule, JsonPipe } from '@angular/common';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { bootstrapPinMapFill, bootstrapSearch } from '@ng-icons/bootstrap-icons';
import { GoogleMapsService } from '../../google-maps/google-maps.service';
import { LoggingService } from '../../logging/logging.service';
import { ToasterService } from '../../toaster/toaster.service';
import { DeliveryLocationOverrideService } from '../../delivery-location-override/delivery-location-override.service';
import { DeliveryLocationOverride } from '../../delivery-location-override/models/delivery-location-override.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CreateDeliveryLocationOverrideModalComponent } from '../../modals/create-delivery-location-override-modal/create-delivery-location-override-modal.component';
import { AddressDetailModel } from '../../master-contact/models/AddressDetailModel';

@Component({
  selector: 'app-address-picker',
  standalone: true,
  imports: [NgIcon, AsyncPipe, NgSelectModule, ReactiveFormsModule, JsonPipe, CommonModule],
  templateUrl: './address-picker.component.html',
  styleUrls: ['./address-picker.component.scss'],
  viewProviders:
    [
      provideIcons({
        bootstrapPinMapFill,
        bootstrapSearch
      })
    ]
})
export class AddressPickerComponent implements OnChanges, OnInit {

  @Input()
  formGroup!: FormGroup;

  @Input()
  label: string = 'Address';

  @Input()
  disableEmitter: EventEmitter<boolean> | undefined;

  @Input()
  textPosition: 'start' | 'end' = 'start';

  @Input()
  showPreciseLocationOptions: boolean = false;

  @Input()
  preciseLocationLabel: string = 'Location';

  masterContactAddresses: MasterContactAddress[] | null = null;

  selectedMasterContact: any = null;

  masterContacts$!: Observable<MasterContactAddress[]>;
  masterContactInput$ = new Subject<string>();
  masterContactsLoading = false;

  private addressLookupVersion = 0;
  private masterContactLookupPending = false;
  private masterContactLoadedValidator = () => this.masterContactLookupPending ? { masterContactLoading: true } : null;

  preciseLocationOptions$!: Observable<DeliveryLocationOverride[]>;
  showPreciseLocationPicker: boolean = true;

  googleAddressSuggestions: any[] | null = null;

  googleAddresses$!: Observable<MasterContactAddress[]>;
  googleAddressInput$ = new Subject<string>();
  googleAddressesLoading = false;

  lockdown: boolean = false;

  @ViewChild('googleAddressPicker') googleAddressPicker!: ElementRef;
  @ViewChild('masterContactPicker') masterContactPicker!: NgSelectComponent;

  constructor(
    private masterContactService: MasterContactService,
    private googleMapsService: GoogleMapsService,
    private fb: FormBuilder,
    private readonly appRef: ApplicationRef,
    private readonly loggingService: LoggingService,
    private readonly toasterService: ToasterService,
    private readonly deliveryLocationOverrideService: DeliveryLocationOverrideService,
    private readonly modalService: NgbModal) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formGroup']) {
      this.formGroup = changes['formGroup'].currentValue;
    }
  }

  ngOnInit() {
    if (this.disableEmitter) {
      this.disableEmitter.subscribe((value) => {
        if (value) {
          this.formGroup.disable({ emitEvent: false });
        } else {
          this.formGroup.enable();
        }
        this.lockdown = value;
      });
    }

    const addressForm = this.formGroup.get('addressSelection') as FormGroup;
    if (this.showPreciseLocationOptions) {
      const preciseControls = this.formGroup.get('preciseLocationOptions') as FormGroup;

      if (preciseControls) {
        preciseControls.addControl('deliveryLocationOverrideId', this.fb.control(null));
      }
    }

    if (addressForm) {
      addressForm.addControl('masterContactAddressId', this.fb.control(null, [Validators.required, this.masterContactLoadedValidator]));
      addressForm.addControl('addressDetails', this.fb.control(null));
      addressForm.addControl('verified', this.fb.control(false));
      addressForm.addControl('isCreateMode', this.fb.control(false));
      addressForm.addControl('addressName', this.fb.control(null));

      this.formGroup.get('addressSelection.masterContactAddressId')?.valueChanges.subscribe(this.refreshPreciseLocationOptions);

      addressForm.get('isCreateMode')?.valueChanges.subscribe((value) => {
        if (value) {
          addressForm.get('masterContactAddressId')?.patchValue(null);
          addressForm.get('masterContactAddressId')?.clearValidators();

          addressForm.get('addressDetails')?.setValidators([Validators.required]);
          addressForm.get('addressName')?.setValidators([Validators.required]);
        } else {
          addressForm.get('addressDetails')?.patchValue(null, { emitEvent: false });
          addressForm.get('addressDetails')?.clearValidators();
          addressForm.get('addressName')?.patchValue(null, { emitEvent: false });
          addressForm.get('addressName')?.clearValidators();
          addressForm.get('verified')?.patchValue(false, { emitEvent: false });

          addressForm.get('masterContactAddressId')?.setValidators([Validators.required, this.masterContactLoadedValidator]);
        }

        addressForm.get('masterContactAddressId')?.updateValueAndValidity({ emitEvent: false });
        addressForm.get('addressDetails')?.updateValueAndValidity({ emitEvent: false });
        addressForm.get('addressName')?.updateValueAndValidity({ emitEvent: false });
      });

      this.formGroup.get('addressSelection.addressDetails')?.valueChanges.subscribe((value) => {
        if (value && value.lat && value.lng) {
          this.googleMapsService.getGoogleAddressDetailsByLatLng(value.lat, value.lng).then((search) => {
            if (search && search.results) {
              const myAddress = search.results[0];
              this.formGroup.get('addressSelection.addressDetails')?.patchValue({
                description: myAddress.formatted_address,
                placeId: myAddress.place_id,
                addressComponents: myAddress.address_components.map((component: any) => {
                  return {
                    longName: component.long_name,
                    shortName: component.short_name,
                    types: component.types
                  };
                }),
                lat: myAddress.geometry.location.lat(),
                lng: myAddress.geometry.location.lng()
              }, { emitEvent: false });
            }
          });
        }
      });
    }
    this.loadMasterContacts();
    this.loadGoogleSuggestions();
  }

  refreshPreciseLocationOptions = (masterContactId: any) => {
    const addressForm = this.formGroup.get('addressSelection') as FormGroup;
    const lookupVersion = ++this.addressLookupVersion;
    this.masterContactLookupPending = false;
    this.preciseLocationOptions$ = of([]);

    if (addressForm) {
      const reqId = typeof masterContactId === 'number' ? masterContactId : masterContactId?.masterContactId;
      const formControl = addressForm.get('masterContactAddressId')!;
      const isCurrentSelection = () => {
        const selected = formControl.value;
        const selectedId = typeof selected === 'number' ? selected : selected?.masterContactId;
        return lookupVersion === this.addressLookupVersion && selectedId === reqId && !addressForm.get('isCreateMode')!.value;
      };
      const clearUnavailableAddress = () => {
        formControl.setValue(null);
        formControl.markAsTouched();
        this.toasterService.showDanger(`Unable to load the ${this.label} address. Please select the address again.`);
      };

      if (!Number.isInteger(reqId) || reqId <= 0) {
        formControl.setValue(null, { emitEvent: false });
        this.formGroup.get('preciseLocationOptions.deliveryLocationOverrideId')?.setValue(null, { emitEvent: false });
        return;
      }

      if (masterContactId) {
        // Prevent submitting a restored default until its contact has been checked.
        this.masterContactLookupPending = true;
        formControl.updateValueAndValidity({ emitEvent: false });
        this.deliveryLocationOverrideService.getDeliveryLocationOverridesByMasterContactId(reqId).subscribe({
          next: (overrides: DeliveryLocationOverride[]) => {
            if (!isCurrentSelection()) {
              return;
            }
            this.preciseLocationOptions$ = of(overrides);
            this.showPreciseLocationPicker = false;
            this.appRef.tick();
            this.showPreciseLocationPicker = true;
          },
          error: (error) => {
            this.loggingService.logEvent('Error loading delivery location overrides: ' + JSON.stringify(error));
          }
        });

        this.masterContactService.getMasterContactById(reqId).subscribe({
          next: (masterContact: MasterContactAddress) => {
            if (!isCurrentSelection()) {
              return;
            }
            if (masterContact && masterContact.masterContactId === reqId) {
              this.masterContactLookupPending = false;
              formControl.patchValue({
                masterContactName: masterContact.masterContactName,
                masterContactId: masterContact.masterContactId,
                masterContactAddress: masterContact.masterContactAddress
              }, { emitEvent: false });
            } else {
              this.loggingService.logEvent('getMasterContactById returned empty or invalid data for ID: ' + reqId);
              clearUnavailableAddress();
            }
          },
          error: (error) => {
            this.loggingService.logEvent('Error loading master contact: ' + JSON.stringify(error));
            if (isCurrentSelection()) {
              clearUnavailableAddress();
            }
          }
        });
      }
    }
  }

  addressSelected() {
    return this.formGroup.get('addressSelection.masterContactAddressId')?.value || this.formGroup.get('addressSelection.addressDetails')?.value;
  }

  compareWith(a: any, b: any) {
    return a.masterContactId === b.masterContactId;
  }

  compareWithDeliveryLocationOverride(a: any, b: any) {
    if (typeof a === 'number' && typeof b === 'number') {
      return a === b;
    }
    if (typeof a === 'number' && b && typeof b === 'object') {
      return a === b.deliveryLocationOverrideId;
    }
    if (a && typeof a === 'object' && typeof b === 'number') {
      return a.deliveryLocationOverrideId === b;
    }
    return a?.deliveryLocationOverrideId === b?.deliveryLocationOverrideId;
  }

  private loadMasterContacts() {
    this.masterContacts$ = concat(
      of([]), // default items
      this.masterContactInput$.pipe(
        // debounce requests for half a second
        distinctUntilChanged(),
        tap(() => (this.masterContactsLoading = true)),
        switchMap((term) =>
          this.masterContactService.getMasterContactAddresses(term).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.masterContactsLoading = false)),
          )
        ),
      ),
    );
  }

  getCreateMode() {
    return this.formGroup.get('addressSelection.isCreateMode');
  }

  searchMasterContactAddresses(term: string, item: MasterContactAddress) {
    term = term.toLowerCase();
    return item.masterContactAddress.toLowerCase().indexOf(term) > -1
      || item.masterContactName.toLowerCase().indexOf(term) > -1;
  }

  toggleCreateMode() {
    this.getCreateMode()?.patchValue(!this.getCreateMode()?.value);

    if (this.getCreateMode()?.value) {
      this.formGroup.get('addressSelection.masterContactAddressId')?.setValidators([]);
      this.formGroup.get('addressSelection.addressDetails')?.setValidators([Validators.required]);
      this.formGroup.get('addressSelection.addressName')?.setValidators([Validators.required]);

      this.appRef.tick();
      (this.googleAddressPicker as any).focus();
    } else {
      this.formGroup.get('addressSelection.masterContactAddressId')?.setValidators([Validators.required, this.masterContactLoadedValidator]);
      this.formGroup.get('addressSelection.addressDetails')?.setValidators([]);
      this.formGroup.get('addressSelection.addressName')?.setValidators([]);

      this.appRef.tick();
      (this.masterContactPicker as any).focus();
    }
  }

  private loadGoogleSuggestions() {
    this.googleAddresses$ = concat(
      of([]), // default items
      this.googleAddressInput$.pipe(
        debounceTime(250), // debounce requests for half a second
        distinctUntilChanged(),
        switchMap((term) =>
          this.googleMapsService.searchGoogleAddresses(term).pipe(
            catchError(() => of([])), // empty list on error
          ),
        ),
      ),
    );
  }

  addressNameAltered() {
    const addressName = this.formGroup.get('addressSelection.addressName')?.value;
    this.formGroup.get('addressSelection.addressName')?.patchValue(addressName?.trim(), { emitEvent: false });
    this.formGroup.get('addressSelection.verified')?.setValue(false);
  }

  compareWithGoogle(a: any, b: any) {
    return a.place_id === b.placeId;
  }

  async googleAddressChanged($event: any) {
    if ($event === undefined) {
      this.formGroup.get('addressSelection.addressName')?.setValue("");
      this.formGroup.get('addressSelection.verified')?.setValue(false);
      return;
    }
    const isPremiseOrAddress = ($event.types as any[]).find((type) => type === 'street_address' || type === 'premise');

    if (!isPremiseOrAddress) {
      this.formGroup.get('addressSelection.addressName')?.setValue($event.structured_formatting.main_text);
      this.formGroup.get('addressSelection.verified')?.setValue(true);
    } else {
      this.formGroup.get('addressSelection.addressName')?.setValue("");
      this.formGroup.get('addressSelection.verified')?.setValue(false);
    }

    const placeId = $event.place_id;
    const isValidPlaceId = await this.googleMapsService.checkGoogleAddressPlaceId(placeId);

    this.preciseLocationOptions$ = of([]);
    this.formGroup.get('preciseLocationOptions.deliveryLocationOverrideId')?.patchValue(null, { emitEvent: false });

    if (!isValidPlaceId) {
      this.loggingService.logTrace('Invalid place_id: ' + placeId);
      this.toasterService.showDanger('Invalid address selected. Please select a valid address from the list. If the issue persists, please contact support.');
      this.formGroup.get('addressSelection')?.patchValue({
        addressDetails: null,
        masterContactAddressId: null,
        verified: false,
        isCreateMode: true
      }, { emitEvent: false });
      this.formGroup.updateValueAndValidity();
      return;
    }

    const { results } = await this.googleMapsService.getGoogleAddressDetails(placeId);

    const details = {
      addressComponents: results[0].address_components.map((component: any) => {
        return {
          longName: component.long_name,
          shortName: component.short_name,
          types: component.types
        };
      }),
      placeId: $event.place_id,
      lat: results[0].geometry.location.lat(),
      lng: results[0].geometry.location.lng()
    };

    this.formGroup.get('addressSelection.addressDetails')?.patchValue(details, { emitEvent: false });
    this.formGroup.get('addressSelection.masterContactAddressId')?.patchValue(null, { emitEvent: false });
  }

  async openCreateNewDeliveryLocationOverrideModal() {
    if (this.getCreateMode()?.value) {

      const lat = this.formGroup.get('addressSelection.addressDetails')?.value?.lat || null;
      const lng = this.formGroup.get('addressSelection.addressDetails')?.value?.lng || null;

      const addressDetails = await this.googleMapsService.getGoogleAddressDetailsByLatLng(lat, lng);

      const addressDetailsReqModel: AddressDetailModel = {
        lat: this.formGroup.get('addressSelection.addressDetails')?.value?.lat || null,
        lng: this.formGroup.get('addressSelection.addressDetails')?.value?.lng || null,
        addressComponents: addressDetails.results[0].address_components.map((component: any) => {
          return {
            longName: component.long_name || component.longName,
            shortName: component.short_name || component.shortName,
            types: component.types
          }
        }),
      }
      this.masterContactService.getFormatedMasterContactAddress(addressDetailsReqModel).subscribe({
        next: (addressString: string) => {
          const modalRef = this.modalService.open(CreateDeliveryLocationOverrideModalComponent, { size: 'xl' });

          modalRef.componentInstance.headerLabel = this.preciseLocationLabel;
          modalRef.componentInstance.latitude = lat;
          modalRef.componentInstance.longitude = lng;
          modalRef.componentInstance.addressName = this.formGroup.get('addressSelection.addressName')?.value || '';
          modalRef.componentInstance.addressDetails = addressString || '';

          modalRef.result.then((result) => {
            if (result.status === 'success') {
              this.toasterService.showDefault('Delivery Location Override created successfully.');

              this.preciseLocationOptions$ = this.preciseLocationOptions$.pipe(
                switchMap((options) => {
                  const newOptions = options ? [...options] : [];
                  newOptions.push({
                    deliveryLocationOverrideId: result.deliveryLocationOverrideData.deliveryLocationOverrideId,
                    name: result.deliveryLocationOverrideData.name,
                    latitude: result.deliveryLocationOverrideData.latitude as number,
                    longitude: result.deliveryLocationOverrideData.longitude as number,
                    address: result.deliveryLocationOverrideData.address,
                    displayName: result.deliveryLocationOverrideData.name,
                    locality: "",
                    region: "",
                    sublocality: ""
                  });
                  return of(newOptions);
                })
              );
              this.appRef.tick();
              this.formGroup.get('preciseLocationOptions.deliveryLocationOverrideId')?.patchValue(result.deliveryLocationOverrideData.deliveryLocationOverrideId);
            }
      }).catch((error) => {
        this.loggingService.logEvent('Error creating Delivery Location Override: ' + JSON.stringify(error));
      });
        },
        error: (error) => {
          this.loggingService.logEvent('Error getting formatted master contact address: ' + JSON.stringify(error));
          this.toasterService.showDanger('Unable to format address. Please try again.');
        }
      });
    } else {
      const masterContactAddress = this.formGroup.get('addressSelection.masterContactAddressId')?.value;
      if (masterContactAddress) {
        this.masterContactService.getMasterContactById(masterContactAddress.masterContactId).subscribe({
          next: (masterContact) => {
            if (!masterContact || !masterContact.masterContactId) {
              this.loggingService.logEvent('getMasterContactById returned empty or invalid data for modal');
              this.toasterService.showDanger('Unable to load address details. Please try again.');
              return;
            }

            const modalRef = this.modalService.open(CreateDeliveryLocationOverrideModalComponent, { size: 'xl' });

            modalRef.componentInstance.headerLabel = this.preciseLocationLabel;
            modalRef.componentInstance.latitude = masterContact.masterLatitude;
            modalRef.componentInstance.longitude = masterContact.masterLongitude;
            modalRef.componentInstance.addressName = masterContact.masterContactName;
            modalRef.componentInstance.addressDetails = masterContact.masterContactAddress;
            modalRef.componentInstance.masterContactId = masterContact.masterContactId;

            modalRef.result.then((result) => {
              if (result.status === 'success') {
                const deliveryLocationOverrideData = result.deliveryLocationOverrideData;

                this.toasterService.showDefault('Delivery Location Override created successfully.');
                this.formGroup.get('preciseLocationOptions.deliveryLocationOverrideId')?.patchValue(deliveryLocationOverrideData.deliveryLocationOverrideId);
                this.refreshPreciseLocationOptions(masterContactAddress.masterContactId);

              }
            }).catch((error) => {
              this.loggingService.logEvent('Error creating Delivery Location Override: ' + JSON.stringify(error));
            });
          },
          error: (error) => {
            this.loggingService.logEvent('Error loading master contact for modal: ' + JSON.stringify(error));
            this.toasterService.showDanger('Unable to load address details. Please try again.');
          }
        });
      }
    }
  }
}
